import { type ComputedRef } from "vue";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { Maybe } from "../../../graphql";
import { useServiceStore } from "../../../store/service";

export type ModResourceId = string | number;

/**
 * The catalogue version of a mod, formatted for display, or `null` when the source has none.
 *
 * Rendered as a bare `v3.1.4` rather than a translated "Version: …" label: a version string reads
 * the same in every language, so it needs no i18n key — which also avoids a missing key rendering
 * as empty in a built bundle — and it costs no horizontal space.
 *
 * Null is common and not an error. Minecraft and Hytale model versions as `availableVersions`
 * instead, and several Workshop-backed catalogues have no version column at all, so the caller
 * should omit the line rather than render an empty one.
 *
 * Shared by the browse tile and the installed row so the two cannot drift apart.
 */
export function formatModVersion(version?: string | null): string | null {
  const raw = version?.trim();
  if (!raw) return null;
  return /^v\d/i.test(raw) ? raw : `v${raw}`;
}

/**
 * Which catalogue a mod's build came from, formatted for display, or `null` when the game has no
 * concept of one (TR-5407).
 *
 * The value is a platform name — Thunderstore, Hexium — so it is rendered literally rather than
 * looked up: a brand stays itself in every locale, and inventing a key per platform would mean a new
 * Loco asset every time a catalogue is added. Only the "Source" label is translated.
 *
 * Valheim is the only game that populates this today. Everything else yields null and the line is
 * omitted, so this costs nothing on the other nine games sharing these components.
 */
export function formatModSource(source?: string | null): string | null {
  const raw = source?.trim();
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

// How loudly a tag reads. Omitted is the neutral chip every existing tag renders as, so adding this
// changed nothing for the games that were already here (TR-5406).
//
// Deliberately three words rather than colour names: a profile says what it means and the component
// decides how that looks, which is what keeps light and dark themes one decision instead of N.
export type ModTagLevel = "success" | "warning" | "danger";

// A mod tag is either a plain label or a label with an optional leading icon and hover text.
// Plain strings stay valid (Minecraft and the base composable return those).
//
// `tooltip` exists because a card has room for a count but not for a list: "Required by 3 mods" is
// the tag, and *which* three is the part the customer actually wants.
export type ModTag = string | { label: string; icon?: IconDefinition; tooltip?: string; level?: ModTagLevel };

/**
 * Interface for the `ModLogo` component (and any other future assets)
 */
export interface IModAsset {
  url: string;
  thumbnailUrl?: Maybe<string>;
}

/**
 * Interface for any URLs the mod may have
 */
export interface IModLinks {
  websiteUrl: string;
}

/**
 * Interface for any author information the mod may have
 */
export interface IModAuthor {
  name: string;
}

/** One mod named by another — a dependency or a dependent. */
export interface RelatedMod {
  workshopId: string;
  name: string;
}

/**
 * A named list of mods related to the one being viewed, for the mod info dialog.
 *
 * Deliberately plain: labelled links and an optional note, nothing a game profile has to model. The
 * dialog turns each into a section with a jump link, in the order given.
 */
export interface ModRelation {
  /** Stable within one dialog; used as the scroll anchor. */
  key: string;
  title: string;
  entries: Array<{ label: string; url?: string; note?: string }>;
}

export interface IModCategory {
  id: ModResourceId;
  name: string;
  // If the category is some kind of higher-level category
  isClass?: boolean;
}

/**
 * Interface for the `Mod` component, extending the base interface
 */
export interface IMod {
  id: ModResourceId;
  name: string;
  summary?: Maybe<string>;
  logo?: Maybe<IModAsset>;
  dateCreated: string;
  dateModified?: Maybe<string>;
  downloadCount?: Maybe<number>;
  authors?: Maybe<Maybe<IModAuthor>[]>;
  links?: Maybe<IModLinks>;
  lastUpdateTime: number;
}

export type IUseMods<ModType extends IMod> = {
  // The title/name of the mod store
  storeTitle: ComputedRef<string>;
  // The list of currently installed mods
  installedMods: ComputedRef<Array<ModType>>;
  // Function to add a mod
  addMod: (mod: ModType) => void;
  // Function to remove a mod
  removeMod: (mod: ModType) => void;
  // Function to extract tags from a mod, if available (plain labels or label+icon)
  modTags?: (mod: ModType) => Array<ModTag>;
  // Optional gate: when it returns false the manage dialog disables the add button
  isModAddable?: (mod: ModType) => boolean;
  // Optional hint shown in the manage dialog (e.g. why a mod can't be added, or client-install notes)
  modHint?: (mod: ModType) => string | undefined;
  // Optional per-mod override of `storeTitle` for the dialog's external link. A game served by a
  // single catalogue leaves this out and the store's name is right for every mod. Valheim merges two
  // (TR-5407), so the button has to name the one the published build actually came from — the href
  // already points there, and a Hexium mod labelled "Thunderstore" reads as a broken link.
  modStoreTitle?: (mod: ModType) => string | undefined;
  // Optional lines shown under the mod's name in the manage list. Unlike a tag, this has room to
  // name other mods — which is what "why is this one here?" needs, and where removal happens.
  modNotes?: (mod: ModType) => Array<string>;
  // Optional lists of related mods, rendered as their own sections in the mod info dialog with a
  // jump link each. Games that do not declare relationships between mods simply return nothing and
  // the dialog looks exactly as it did.
  modRelations?: (mod: ModType) => Array<ModRelation>;
  // Optional description of the not-yet-saved part of the selection, for a game whose panel can ask
  // the backend what it will say about it. Data only: this composable is called during render, so
  // it must not own the request.
  pendingIssuesInput?: ComputedRef<{ workshopIds: string[]; providedModIds: string[]; buildMajor?: number | null }>;
  // Optional: drop a single enabled mod id, for a game whose "switched on" list can hold ids that
  // belong to no installed mod. Editing that list is otherwise not the customer's job.
  removeModId?: (modId: string) => void;
  isModIdActive?: (modId?: string | null) => boolean;
};

export function useMods() {
  const service = useServiceStore();
  return service.ctx.gameProfile?.useMods!();
}
